import { useState, useEffect } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";
import api, { formatCurrency, formatDate } from "../lib/api";
import { toast } from "sonner";
import FollowUpReminders from "../components/FollowUpReminders";
import PortfolioGrowthHero from "../components/PortfolioGrowthHero";
import OpsPulseWidget from "../components/OpsPulseWidget";
import TwilioHealthTile from "../components/TwilioHealthTile";
import { 
  Users,
  Wallet,
  FileCheck,
  CreditCard,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw
} from "lucide-react";
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from "recharts";

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = async () => {
    try {
      const response = await api.get("/dashboard/admin");
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const statCards = stats ? [
    { 
      title: "Total Users", 
      value: stats.total_users, 
      icon: Users, 
      change: `${stats.active_users} active`,
      color: "text-gold" 
    },
    { 
      title: "Total Deposits", 
      value: formatCurrency(stats.total_deposits), 
      icon: Wallet, 
      change: "All time",
      color: "text-emerald" 
    },
    {
      title: "Pending KYC",
      value: stats.pending_kyc, 
      icon: FileCheck, 
      change: "Awaiting review",
      color: "text-orange-400" 
    },
    { 
      title: "Pending Payments", 
      value: stats.pending_payments, 
      icon: CreditCard, 
      change: "To be processed",
      color: "text-blue-400" 
    },
  ] : [];

  // Mock chart data for demo
  const chartData = [
    { month: "Jan", deposits: 45000 },
    { month: "Feb", deposits: 52000 },
    { month: "Mar", deposits: 48000 },
    { month: "Apr", deposits: 61000 },
    { month: "May", deposits: 55000 },
    { month: "Jun", deposits: 72000 },
  ];

  if (loading) {
    return (
      <DashboardLayout title="Dashboard" subtitle="Loading...">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <Card key={`admin-skel-${i}`} className="bg-surface border-white/5">
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-white/10 rounded w-24 mb-4"></div>
                  <div className="h-8 bg-white/10 rounded w-16"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title={`Welcome, ${user?.full_name}`} 
      subtitle={`${user?.role === "superuser" ? "Superuser" : "Administrator"} Dashboard`}
    >
      {/* Platform Growth Hero - combined AUM with emerald glow when scaling.
          Monaxa (FORGE-20260522-001) is intentionally omitted from this hero
          while accounts are deterministic demo data; the dedicated
          /partner-account page still surfaces demo metrics with disclosure. */}
      <PortfolioGrowthHero
        scope="platform"
        label="Platform AUM"
        caption="Native Fund assets under management."
      />

      {/* Ops Pulse — at-a-glance admin controls */}
      <div className="grid grid-cols-1 gap-4 mb-6">
        <OpsPulseWidget />
      </div>

      {/* Twilio Health — voice/SMS ops snapshot */}
      <div className="mb-6">
        <TwilioHealthTile />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, index) => (
          <Card 
            key={stat.title} 
            className="bg-surface border-white/5 card-hover animate-fade-in"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-10 h-10 rounded bg-white/5 flex items-center justify-center ${stat.color}`}>
                  <stat.icon size={20} />
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-900 tabular-nums">{stat.value}</div>
              <p className="text-sm text-muted-foreground mt-1">{stat.title}</p>
              <p className="text-xs text-gray-500 mt-2">{stat.change}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6 mb-8">
        <Card className="bg-surface border-white/5">
          <CardHeader>
            <CardTitle className="text-white font-serif flex items-center justify-between">
              <span>Deposits Overview</span>
              <Button variant="ghost" size="sm" onClick={fetchDashboard} data-testid="refresh-dashboard">
                <RefreshCw size={16} />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorDeposits" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EAB308" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#EAB308" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis dataKey="month" stroke="#71717A" fontSize={12} />
                <YAxis stroke="#71717A" fontSize={12} tickFormatter={(v) => `$${v/1000}k`} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#121212', border: '1px solid #27272A', borderRadius: '4px' }}
                  labelStyle={{ color: '#EDEDED' }}
                  formatter={(value) => [formatCurrency(value), 'Deposits']}
                />
                <Area 
                  type="monotone" 
                  dataKey="deposits" 
                  stroke="#EAB308" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#colorDeposits)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

      </div>

      {/* Recent Users */}
      <Card className="bg-surface border-white/5">
        <CardHeader>
          <CardTitle className="text-white font-serif">Recent Users</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="recent-users-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">User</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Role</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">KYC Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Joined</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Referral Code</th>
                </tr>
              </thead>
              <tbody>
                {stats?.recent_users?.map((u) => (
                  <tr key={u.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-white font-medium">{u.full_name}</p>
                        <p className="text-gray-500 text-sm">{u.email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant={u.role === "admin" || u.role === "superuser" ? "default" : "secondary"} className={u.role === "admin" ? "bg-gold/20 text-gold" : u.role === "partner" ? "bg-teal-500/20 text-teal-400" : ""}>
                        {u.role}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <Badge 
                        variant={u.kyc_status === "approved" ? "default" : "secondary"}
                        className={u.kyc_status === "approved" ? "bg-emerald/20 text-emerald" : u.kyc_status === "pending" ? "bg-orange-500/20 text-orange-400" : ""}
                      >
                        {u.kyc_status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-gray-400 text-sm">{formatDate(u.created_at)}</td>
                    <td className="py-3 px-4">
                      <code className="text-xs bg-white/5 px-2 py-1 rounded text-gold">{u.referral_token}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Follow-Up Reminders */}
      <div className="mt-8">
        <FollowUpReminders compact={true} />
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
